import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  AlertTriangle,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  ClipboardCheck,
  ExternalLink,
  FileCheck2,
  Gauge,
  Loader2,
  Search,
  Send,
  UserCheck,
  XCircle,
} from "lucide-react";

import { api } from "../../api";

import {
  cleanServiceNumber,
  formatServiceNumber,
  isValidServiceNumber,
} from "../../context/TrainingSessionContext";

import {
  LEADING_SECTIONS,
  LEADING_TASKS,
} from "../../pages/site/leading";

const PDF_PATH =
  "/training-assets/leading-cadet-notebook.pdf";

function formatDate(value) {
  if (!value) return "—";

  try {
    return new Intl.DateTimeFormat(
      "en-GB",
      {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }
    ).format(new Date(value));
  } catch {
    return value;
  }
}

function taskStatus(
  task,
  submittedAt,
  module
) {
  if (
    module?.status ===
      "complete" ||
    module?.review_outcome ===
      "approved"
  ) {
    return {
      key: "approved",
      label: "Approved",
      className:
        "bg-emerald-100 text-emerald-800 border-emerald-200",
    };
  }

  if (
    module?.review_outcome ===
    "returned"
  ) {
    const assessed =
      module.assessed_at
        ? new Date(
            module.assessed_at
          ).getTime()
        : 0;

    const submitted =
      submittedAt
        ? new Date(
            submittedAt
          ).getTime()
        : 0;

    if (submitted > assessed) {
      return {
        key: "resubmitted",
        label: "Resubmitted",
        className:
          "bg-blue-100 text-blue-800 border-blue-200",
      };
    }

    return {
      key: "returned",
      label: "Returned",
      className:
        "bg-red-100 text-red-800 border-red-200",
    };
  }

  if (submittedAt) {
    return {
      key: "submitted",
      label: "Awaiting check",
      className:
        "bg-amber-100 text-amber-900 border-amber-200",
    };
  }

  if (
    module?.status ===
    "in_progress"
  ) {
    return {
      key: "progress",
      label: "In progress",
      className:
        "bg-slate-100 text-slate-700 border-slate-200",
    };
  }

  return {
    key: "not_started",
    label: "Not started",
    className:
      "bg-slate-50 text-slate-500 border-slate-200",
  };
}

function StatusBadge({
  status,
}) {
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-bold ${status.className}`}
    >
      {status.label}
    </span>
  );
}

function AssessorDetails({
  name,
  rank,
  setName,
  setRank,
}) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4">
      <div className="flex items-center gap-2">
        <UserCheck className="w-5 h-5 text-raf-navy" />

        <h3 className="font-black text-raf-navy">
          Instructor
        </h3>
      </div>

      <p className="text-sm text-slate-500 mt-1">
        These details are recorded
        against each electronic
        assessment.
      </p>

      <div className="mt-4 grid sm:grid-cols-2 gap-3">
        <label>
          <span className="text-sm font-bold text-raf-navy">
            Name
          </span>

          <input
            type="text"
            value={name}
            onChange={(event) =>
              setName(
                event.target.value
              )
            }
            placeholder="e.g. Ben Wordsworth"
            className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2.5 outline-none focus:border-raf-blue"
          />
        </label>

        <label>
          <span className="text-sm font-bold text-raf-navy">
            Rank / role
          </span>

          <input
            type="text"
            value={rank}
            onChange={(event) =>
              setRank(
                event.target.value
              )
            }
            placeholder="e.g. Fg Off"
            className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2.5 outline-none focus:border-raf-blue"
          />
        </label>
      </div>
    </div>
  );
}

function AnswerDisplay({
  task,
  answers,
}) {
  return (
    <div className="grid md:grid-cols-2 gap-3">
      {task.fields.map(
        (definition) => {
          const value =
            answers?.[
              definition.key
            ];

          return (
            <div
              key={
                definition.key
              }
              className="rounded-xl border border-slate-200 bg-slate-50 p-4"
            >
              <p className="text-xs uppercase tracking-wider text-slate-500 font-bold">
                {
                  definition.label
                }
              </p>

              <p className="text-sm text-slate-900 mt-2 whitespace-pre-wrap min-h-5">
                {String(
                  value || ""
                ).trim() ||
                  "No answer entered"}
              </p>
            </div>
          );
        }
      )}
    </div>
  );
}

function AssessmentHistory({
  module,
}) {
  const history =
    Array.isArray(
      module?.assessment_history
    )
      ? [
          ...module.assessment_history,
        ].reverse()
      : [];

  if (!history.length) {
    return null;
  }

  return (
    <div className="mt-5">
      <h4 className="font-black text-raf-navy">
        Assessment history
      </h4>

      <div className="mt-3 space-y-2">
        {history.map(
          (entry, index) => (
            <div
              key={`${entry.assessed_at}-${index}`}
              className="border border-slate-200 rounded-lg p-3"
            >
              <div className="flex flex-wrap items-center gap-2">
                <StatusBadge
                  status={
                    entry.outcome ===
                    "approved"
                      ? {
                          label:
                            "Approved",
                          className:
                            "bg-emerald-100 text-emerald-800 border-emerald-200",
                        }
                      : {
                          label:
                            "Returned",
                          className:
                            "bg-red-100 text-red-800 border-red-200",
                        }
                  }
                />

                <span className="text-xs text-slate-500">
                  {formatDate(
                    entry.assessed_at
                  )}
                </span>
              </div>

              <p className="text-sm font-semibold text-slate-700 mt-2">
                {
                  entry.assessor_rank
                }{" "}
                {
                  entry.assessor_name
                }
              </p>

              {entry.feedback && (
                <p className="text-sm text-slate-600 mt-2 whitespace-pre-wrap">
                  {entry.feedback}
                </p>
              )}
            </div>
          )
        )}
      </div>
    </div>
  );
}

export default function LeadingAssessmentPanel({
  initialServiceNumber = "",
  onClose = null,
}) {
  const [lookup, setLookup] =
    useState(
      initialServiceNumber
    );

  const [record, setRecord] =
    useState(null);

  const [loading, setLoading] =
    useState(false);

  const [error, setError] =
    useState("");

  const [success, setSuccess] =
    useState("");

  const [selectedSection, setSelectedSection] =
    useState(
      "airmanship"
    );

  const [selectedTaskId, setSelectedTaskId] =
    useState(
      LEADING_TASKS[0]?.id ||
        ""
    );

  const [showOptional, setShowOptional] =
    useState(false);

  const [feedback, setFeedback] =
    useState("");

  const [saving, setSaving] =
    useState(false);

  const [assessorName, setAssessorName] =
    useState(
      () =>
        sessionStorage.getItem(
          "1471_assessor_name"
        ) || ""
    );

  const [assessorRank, setAssessorRank] =
    useState(
      () =>
        sessionStorage.getItem(
          "1471_assessor_rank"
        ) || ""
    );

  useEffect(() => {
    sessionStorage.setItem(
      "1471_assessor_name",
      assessorName
    );
  }, [assessorName]);

  useEffect(() => {
    sessionStorage.setItem(
      "1471_assessor_rank",
      assessorRank
    );
  }, [assessorRank]);

  const openRecord =
    useCallback(
      async (
        serviceNumber
      ) => {
        setLoading(true);
        setError("");
        setSuccess("");

        try {
          const { data } =
            await api.get(
              `/cadet-training/records/${serviceNumber}`
            );

          setRecord(data);
          setLookup(
            data.service_number
          );

          const state =
            data?.activities
              ?.leading
              ?.state || {};

          const current =
            data?.activities
              ?.leading
              ?.current_step;

          if (
            current &&
            LEADING_TASKS.some(
              (task) =>
                task.id ===
                current
            )
          ) {
            const task =
              LEADING_TASKS.find(
                (item) =>
                  item.id ===
                  current
              );

            setSelectedTaskId(
              current
            );

            setSelectedSection(
              task.section
            );
          } else {
            const firstSubmitted =
              LEADING_TASKS.find(
                (task) =>
                  state
                    ?.leadingSubmitted?.[
                    task.id
                  ]
              );

            const fallback =
              firstSubmitted ||
              LEADING_TASKS[0];

            if (fallback) {
              setSelectedTaskId(
                fallback.id
              );

              setSelectedSection(
                fallback.section
              );
            }
          }
        } catch (err) {
          setRecord(null);

          setError(
            err?.response?.data
              ?.detail ||
              "Unable to open that training record."
          );
        } finally {
          setLoading(false);
        }
      },
      []
    );

  useEffect(() => {
    const number =
      cleanServiceNumber(
        initialServiceNumber
      );

    if (
      number &&
      isValidServiceNumber(
        number
      )
    ) {
      openRecord(number);
    }
  }, [
    initialServiceNumber,
    openRecord,
  ]);

  const leading =
    record?.activities
      ?.leading || {};

  const state =
    leading.state || {};

  const answers =
    state.leadingAnswers || {};

  const submitted =
    state.leadingSubmitted || {};

  const modules =
    useMemo(() => {
      const result = {};

      for (const module of
        leading.modules || []) {
        result[
          module.module_id
        ] = module;
      }

      return result;
    }, [leading.modules]);

  const visibleTasks =
    useMemo(
      () =>
        LEADING_TASKS.filter(
          (task) =>
            task.section ===
              selectedSection &&
            (showOptional ||
              task.mandatory)
        ),
      [
        selectedSection,
        showOptional,
      ]
    );

  useEffect(() => {
    if (
      !visibleTasks.some(
        (task) =>
          task.id ===
          selectedTaskId
      )
    ) {
      const next =
        visibleTasks[0];

      if (next) {
        setSelectedTaskId(
          next.id
        );
      }
    }
  }, [
    selectedTaskId,
    visibleTasks,
  ]);

  const selectedTask =
    LEADING_TASKS.find(
      (task) =>
        task.id ===
        selectedTaskId
    ) ||
    visibleTasks[0];

  const selectedModule =
    selectedTask
      ? modules[
          selectedTask.id
        ] || null
      : null;

  const selectedStatus =
    selectedTask
      ? taskStatus(
          selectedTask,
          submitted[
            selectedTask.id
          ],
          selectedModule
        )
      : null;

  useEffect(() => {
    setFeedback(
      selectedModule
        ?.review_feedback ||
        ""
    );

    setSuccess("");
    setError("");
  }, [
    selectedModule,
    selectedTaskId,
  ]);

  const assess = async (
    outcome
  ) => {
    if (
      !record ||
      !selectedTask
    ) {
      return;
    }

    setError("");
    setSuccess("");

    if (
      !assessorName.trim() ||
      !assessorRank.trim()
    ) {
      setError(
        "Enter the instructor's name and rank/role before marking the activity."
      );
      return;
    }

    if (
      outcome ===
        "returned" &&
      !feedback.trim()
    ) {
      setError(
        "Add feedback explaining what the cadet needs to change."
      );
      return;
    }

    setSaving(true);

    try {
      await api.put(
        `/cadet-training/records/${record.service_number}/assessment`,
        {
          activity:
            "leading",
          module_id:
            selectedTask.id,
          outcome,
          assessor_name:
            assessorName.trim(),
          assessor_rank:
            assessorRank.trim(),
          feedback:
            feedback.trim(),
        }
      );

      await openRecord(
        record.service_number
      );

      setSuccess(
        outcome ===
          "approved"
          ? "Activity approved. The cadet will see the result when they next open Leading Cadet."
          : "Activity returned for changes. The cadet will see your feedback when they next open Leading Cadet."
      );
    } catch (err) {
      setError(
        err?.response?.data
          ?.detail ||
          "Unable to save the assessment."
      );
    } finally {
      setSaving(false);
    }
  };

  const selectedIndex =
    visibleTasks.findIndex(
      (task) =>
        task.id ===
        selectedTask?.id
    );

  const essentialCount =
    LEADING_TASKS.filter(
      (task) =>
        task.mandatory
    ).length;

  const submittedCount =
    LEADING_TASKS.filter(
      (task) =>
        task.mandatory &&
        submitted[
          task.id
        ]
    ).length;

  const approvedCount =
    LEADING_TASKS.filter(
      (task) =>
        task.mandatory &&
        (
          modules[
            task.id
          ]?.review_outcome ===
            "approved" ||
          modules[
            task.id
          ]?.status ===
            "complete"
        )
    ).length;

  const submitLookup = (
    event
  ) => {
    event.preventDefault();

    const number =
      cleanServiceNumber(
        lookup
      );

    if (
      !isValidServiceNumber(
        number
      )
    ) {
      setError(
        "Enter a valid 1471 service number."
      );
      return;
    }

    openRecord(number);
  };

  return (
    <div className="space-y-5">
      <div className="bg-raf-navy text-white rounded-xl p-5 md:p-6 border-b-4 border-raf-red">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-xl bg-white/10 flex items-center justify-center shrink-0">
              <Gauge size={23} />
            </div>

            <div>
              <p className="text-xs uppercase tracking-widest text-white/60 font-bold">
                Instructor marking
              </p>

              <h1 className="text-2xl font-black mt-1">
                Leading Cadet
                Electronic
                Notebook
              </h1>

              <p className="text-white/70 text-sm mt-2">
                Review Essential
                Knowledge and
                optional Develop
                Understanding work.
              </p>
            </div>
          </div>

          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-white/30 px-3 py-2 text-sm font-semibold"
            >
              <ChevronLeft
                size={16}
              />
              Back to Records
            </button>
          )}
        </div>
      </div>

      <AssessorDetails
        name={
          assessorName
        }
        rank={
          assessorRank
        }
        setName={
          setAssessorName
        }
        setRank={
          setAssessorRank
        }
      />

      <form
        onSubmit={
          submitLookup
        }
        className="bg-white border border-slate-200 rounded-xl p-5"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-raf-navy text-white flex items-center justify-center">
            <Search size={19} />
          </div>

          <div>
            <h2 className="font-black text-xl text-raf-navy">
              Open a cadet's
              Leading notebook
            </h2>

            <p className="text-sm text-slate-500 mt-1">
              Enter the cadet's 1471
              service number.
            </p>
          </div>
        </div>

        <div className="mt-4 flex flex-col sm:flex-row gap-2">
          <input
            type="text"
            inputMode="numeric"
            autoComplete="off"
            spellCheck={false}
            value={
              formatServiceNumber(
                cleanServiceNumber(
                  lookup
                )
              )
            }
            onChange={(event) =>
              setLookup(
                cleanServiceNumber(
                  event.target.value
                )
              )
            }
            placeholder="30 1471 0026 001"
            className="flex-1 border-2 border-slate-300 rounded-xl px-4 py-3 text-xl font-black tracking-wider outline-none focus:border-raf-blue"
          />

          <button
            type="submit"
            disabled={loading}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-raf-red px-5 py-3 font-black text-white disabled:opacity-50"
          >
            {loading ? (
              <Loader2
                size={18}
                className="animate-spin"
              />
            ) : (
              <Gauge
                size={18}
              />
            )}

            Open Leading
            Notebook
          </button>
        </div>

        {!record &&
          error && (
            <div className="mt-4 rounded-xl border border-red-200 bg-red-50 p-3 flex gap-2">
              <AlertTriangle className="w-5 h-5 text-red-600 shrink-0" />

              <p className="text-sm text-red-800">
                {error}
              </p>
            </div>
          )}
      </form>

      {record && (
        <>
          <div className="bg-white border border-slate-200 rounded-xl p-4 md:p-5">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div>
                <p className="text-xs uppercase tracking-widest text-slate-500 font-bold">
                  Open training
                  record
                </p>

                <p className="text-2xl font-black tracking-wider text-raf-navy mt-1">
                  {formatServiceNumber(
                    record.service_number
                  )}
                </p>
              </div>

              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="bg-slate-50 rounded-lg px-3 py-2">
                  <p className="text-lg font-black text-raf-navy">
                    {
                      essentialCount
                    }
                  </p>

                  <p className="text-[10px] uppercase tracking-wider text-slate-500">
                    Essential
                  </p>
                </div>

                <div className="bg-amber-50 rounded-lg px-3 py-2">
                  <p className="text-lg font-black text-amber-800">
                    {
                      submittedCount
                    }
                  </p>

                  <p className="text-[10px] uppercase tracking-wider text-amber-700">
                    Submitted
                  </p>
                </div>

                <div className="bg-emerald-50 rounded-lg px-3 py-2">
                  <p className="text-lg font-black text-emerald-800">
                    {
                      approvedCount
                    }
                  </p>

                  <p className="text-[10px] uppercase tracking-wider text-emerald-700">
                    Approved
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="grid lg:grid-cols-[300px_minmax(0,1fr)] gap-5">
            <aside className="space-y-4 lg:sticky lg:top-4 lg:self-start">
              <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                <div className="bg-slate-50 border-b border-slate-200 px-4 py-3">
                  <p className="text-xs uppercase tracking-widest text-slate-500 font-bold">
                    Subjects
                  </p>
                </div>

                <div className="p-2">
                  {LEADING_SECTIONS.map(
                    (section) => {
                      const Icon =
                        section.icon;

                      const active =
                        selectedSection ===
                        section.id;

                      return (
                        <button
                          key={
                            section.id
                          }
                          type="button"
                          onClick={() => {
                            setSelectedSection(
                              section.id
                            );

                            const first =
                              LEADING_TASKS.find(
                                (task) =>
                                  task.section ===
                                    section.id &&
                                  (showOptional ||
                                    task.mandatory)
                              );

                            if (first) {
                              setSelectedTaskId(
                                first.id
                              );
                            }
                          }}
                          className={[
                            "w-full rounded-lg p-3 text-left flex items-center gap-3 transition",
                            active
                              ? "bg-raf-navy text-white"
                              : "hover:bg-slate-50 text-slate-700",
                          ].join(" ")}
                        >
                          <Icon
                            size={18}
                          />

                          <span className="font-semibold text-sm">
                            {
                              section.title
                            }
                          </span>
                        </button>
                      );
                    }
                  )}
                </div>
              </div>

              <button
                type="button"
                onClick={() =>
                  setShowOptional(
                    (previous) =>
                      !previous
                  )
                }
                className={[
                  "w-full rounded-xl border p-4 text-left",
                  showOptional
                    ? "border-blue-300 bg-blue-50"
                    : "border-slate-200 bg-white",
                ].join(" ")}
              >
                <p className="font-bold text-raf-navy">
                  Develop
                  Understanding
                </p>

                <p className="text-sm text-slate-500 mt-1">
                  {showOptional
                    ? "Optional activities are shown."
                    : "Showing Essential Knowledge only."}
                </p>
              </button>

              <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                <div className="bg-slate-50 border-b border-slate-200 px-4 py-3">
                  <p className="text-xs uppercase tracking-widest text-slate-500 font-bold">
                    Activities
                  </p>
                </div>

                <div className="p-2 max-h-[55vh] overflow-y-auto">
                  {visibleTasks.map(
                    (task) => {
                      const module =
                        modules[
                          task.id
                        ];

                      const status =
                        taskStatus(
                          task,
                          submitted[
                            task.id
                          ],
                          module
                        );

                      const active =
                        selectedTask?.id ===
                        task.id;

                      return (
                        <button
                          key={
                            task.id
                          }
                          type="button"
                          onClick={() =>
                            setSelectedTaskId(
                              task.id
                            )
                          }
                          className={[
                            "w-full rounded-lg border p-3 text-left mb-2 transition",
                            active
                              ? "border-raf-blue bg-blue-50"
                              : "border-transparent hover:bg-slate-50",
                          ].join(" ")}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <p className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">
                                {
                                  task.code
                                }
                              </p>

                              <p className="text-sm font-semibold text-raf-navy mt-0.5">
                                {
                                  task.title
                                }
                              </p>
                            </div>

                            <span
                              className={[
                                "w-2.5 h-2.5 rounded-full shrink-0 mt-1.5",
                                status.key ===
                                  "approved"
                                  ? "bg-emerald-500"
                                  : status.key ===
                                      "returned"
                                    ? "bg-red-500"
                                    : status.key ===
                                        "submitted" ||
                                      status.key ===
                                        "resubmitted"
                                      ? "bg-amber-500"
                                      : "bg-slate-300",
                              ].join(" ")}
                              title={
                                status.label
                              }
                            />
                          </div>
                        </button>
                      );
                    }
                  )}
                </div>
              </div>
            </aside>

            <main className="min-w-0">
              {selectedTask && (
                <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
                  <div className="p-5 md:p-6 border-b border-slate-200">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                      <div>
                        <p className="text-xs uppercase tracking-widest text-slate-500 font-bold">
                          {
                            selectedTask.code
                          }
                        </p>

                        <h2 className="text-2xl font-black text-raf-navy mt-1">
                          {
                            selectedTask.title
                          }
                        </h2>

                        <p className="text-sm text-slate-600 mt-2 leading-relaxed">
                          {
                            selectedTask.prompt
                          }
                        </p>
                      </div>

                      <StatusBadge
                        status={
                          selectedStatus
                        }
                      />
                    </div>

                    <div className="mt-4 flex flex-wrap gap-2">
                      <a
                        href={`${PDF_PATH}#page=${selectedTask.page}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-raf-navy"
                      >
                        <ExternalLink
                          size={15}
                        />
                        Original page{" "}
                        {
                          selectedTask.page
                        }
                      </a>

                      {submitted[
                        selectedTask.id
                      ] && (
                        <span className="inline-flex items-center rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-500">
                          Submitted{" "}
                          {formatDate(
                            submitted[
                              selectedTask.id
                            ]
                          )}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="p-5 md:p-6">
                    <h3 className="font-black text-raf-navy">
                      Cadet's work
                    </h3>

                    <div className="mt-4">
                      <AnswerDisplay
                        task={
                          selectedTask
                        }
                        answers={
                          answers[
                            selectedTask.id
                          ] || {}
                        }
                      />
                    </div>

                    <AssessmentHistory
                      module={
                        selectedModule
                      }
                    />

                    <div className="mt-6 border-t border-slate-200 pt-5">
                      <h3 className="font-black text-raf-navy">
                        Instructor
                        feedback
                      </h3>

                      <p className="text-sm text-slate-500 mt-1">
                        This feedback
                        is shown to the
                        cadet next time
                        they open the
                        same Leading
                        activity.
                      </p>

                      <textarea
                        rows={5}
                        value={
                          feedback
                        }
                        onChange={(
                          event
                        ) =>
                          setFeedback(
                            event.target.value
                          )
                        }
                        placeholder="Add feedback, corrections or guidance..."
                        className="mt-3 w-full rounded-xl border border-slate-300 px-4 py-3 outline-none focus:border-raf-blue resize-y"
                      />

                      {error && (
                        <div className="mt-4 rounded-xl border border-red-200 bg-red-50 p-3 flex gap-2">
                          <AlertTriangle className="w-5 h-5 text-red-600 shrink-0" />

                          <p className="text-sm text-red-800">
                            {
                              error
                            }
                          </p>
                        </div>
                      )}

                      {success && (
                        <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 p-3 flex gap-2">
                          <CheckCircle2 className="w-5 h-5 text-emerald-700 shrink-0" />

                          <p className="text-sm text-emerald-900">
                            {
                              success
                            }
                          </p>
                        </div>
                      )}

                      <div className="mt-4 grid sm:grid-cols-2 gap-3">
                        <button
                          type="button"
                          onClick={() =>
                            assess(
                              "returned"
                            )
                          }
                          disabled={
                            saving
                          }
                          className="inline-flex items-center justify-center gap-2 rounded-xl border-2 border-red-300 bg-red-50 px-4 py-3 font-black text-red-800 disabled:opacity-50"
                        >
                          {saving ? (
                            <Loader2
                              size={18}
                              className="animate-spin"
                            />
                          ) : (
                            <XCircle
                              size={18}
                            />
                          )}

                          Return for
                          Changes
                        </button>

                        <button
                          type="button"
                          onClick={() =>
                            assess(
                              "approved"
                            )
                          }
                          disabled={
                            saving
                          }
                          className="inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-700 px-4 py-3 font-black text-white disabled:opacity-50"
                        >
                          {saving ? (
                            <Loader2
                              size={18}
                              className="animate-spin"
                            />
                          ) : (
                            <FileCheck2
                              size={18}
                            />
                          )}

                          Approve
                          Activity
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="mt-4 flex items-center justify-between gap-3">
                <button
                  type="button"
                  disabled={
                    selectedIndex <=
                    0
                  }
                  onClick={() => {
                    if (
                      selectedIndex >
                      0
                    ) {
                      setSelectedTaskId(
                        visibleTasks[
                          selectedIndex -
                            1
                        ].id
                      );
                    }
                  }}
                  className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2.5 font-semibold text-raf-navy disabled:opacity-40"
                >
                  <ChevronLeft
                    size={17}
                  />
                  Previous
                </button>

                <button
                  type="button"
                  disabled={
                    selectedIndex <
                      0 ||
                    selectedIndex >=
                      visibleTasks.length -
                        1
                  }
                  onClick={() => {
                    if (
                      selectedIndex >=
                        0 &&
                      selectedIndex <
                        visibleTasks.length -
                          1
                    ) {
                      setSelectedTaskId(
                        visibleTasks[
                          selectedIndex +
                            1
                        ].id
                      );
                    }
                  }}
                  className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2.5 font-semibold text-raf-navy disabled:opacity-40"
                >
                  Next
                  <ChevronRight
                    size={17}
                  />
                </button>
              </div>

              <div className="mt-5 rounded-xl border border-blue-200 bg-blue-50 p-4">
                <div className="flex gap-3">
                  <Send className="w-5 h-5 text-blue-800 shrink-0" />

                  <p className="text-sm text-blue-950">
                    The assessment is
                    stored against the
                    service number and
                    will follow the
                    cadet to any
                    authorised
                    training tablet.
                  </p>
                </div>
              </div>
            </main>
          </div>
        </>
      )}
    </div>
  );
}